import { ImsPermissions } from "@redotech/account-common/permissions";
import { TeamType } from "@redotech/account-common/team-type";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { folderTypes } from "@redotech/redo-views-common/folders";
import type { ConsolidationView } from "@redotech/redo-views-common/views";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CreateOmsViewFolderModal } from "../../common/create-oms-view-folder-modal";
import { EditOmsViewFolderModal } from "../../common/edit-oms-view-folder-modal";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

type ConsolidationFolder = folderTypes["consolidations"];

const COUNTS_REFRESH_MS = 60_000;

export interface ConsolidationViewsData {
  folders: ConsolidationFolder[];
  handleDeleteFolder: (folderId: string) => void;
  hasInitiallyLoaded: boolean;
  loadError: unknown;
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  updateOrdering: (
    views: ConsolidationView[],
    folders: ConsolidationFolder[],
  ) => void;
  views: ConsolidationView[];
}

export const ConsolidationViewsContext = createContext<
  ConsolidationViewsData | undefined
>(undefined);

export const ConsolidationViewCountsContext = createContext<
  | { folderCounts: Record<string, number>; viewCounts: Record<string, number> }
  | undefined
>(undefined);

export const ReloadConsolidationViewsContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

export function useConsolidationsEnabled(): boolean {
  const team = useContext(TeamContext);
  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  return (
    team?.teamType === TeamType.FulfillmentService &&
    !!team.settings.ims?.enabled &&
    canAccessInventory
  );
}

export const ConsolidationViewsProvider = memo(
  function ConsolidationViewsProvider({ children }: { children: ReactNode }) {
    const client = useContext(RedoOutboundLabelsRpcClientContext);
    const enabled = useConsolidationsEnabled();
    const [views, setViews] = useState<ConsolidationView[]>([]);
    const [folders, setFolders] = useState<ConsolidationFolder[]>([]);
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
    const [loadError, setLoadError] = useState<unknown>();
    const [countsTick, setCountsTick] = useState(0);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [editingFolder, setEditingFolder] =
      useState<ConsolidationFolder | null>(null);

    const latestRequest = useRef(0);
    const reloadViews = useHandler(async () => {
      if (!client) {
        return;
      }
      const request = ++latestRequest.current;
      setLoadError(undefined);
      try {
        const result = await client.getConsolidationViewsAndFolders(null);
        if (request === latestRequest.current) {
          setViews(result.views);
          setFolders(result.folders);
          setHasInitiallyLoaded(true);
        }
      } catch (error) {
        console.error("Error loading consolidation views", error);
        if (request === latestRequest.current) {
          setLoadError(error);
        }
      }
    });

    useEffect(() => {
      if (enabled) {
        void reloadViews();
      }
    }, [client, enabled, reloadViews]);

    useEffect(() => {
      if (!enabled && !hasInitiallyLoaded) {
        return;
      }
      const timer = setInterval(
        () => setCountsTick((tick) => tick + 1),
        COUNTS_REFRESH_MS,
      );
      return () => clearInterval(timer);
    }, [enabled, hasInitiallyLoaded]);

    const countsLoad = useLoad(
      async (signal) => {
        if (!client || !hasInitiallyLoaded) {
          return undefined;
        }
        return client.getConsolidationViewCounts({ search: null }, { signal });
      },
      [client, hasInitiallyLoaded, views, countsTick],
    );
    const [counts, setCounts] = useState<{
      folderCounts: Record<string, number>;
      viewCounts: Record<string, number>;
    }>({ folderCounts: {}, viewCounts: {} });
    useEffect(() => {
      if (countsLoad.value) {
        setCounts(countsLoad.value);
      }
    }, [countsLoad.value]);

    const handleCreateFolder = useHandler(async (name: string) => {
      await client?.createConsolidationViewFolder({ name });
      await reloadViews();
    });
    const handleEditFolder = useHandler(
      async (folderId: string, name: string) => {
        await client?.updateConsolidationViewFolder({ folderId, name });
        await reloadViews();
      },
    );
    const handleDeleteFolder = useHandler(async (folderId: string) => {
      await client?.deleteConsolidationViewFolder({ folderId });
      await reloadViews();
    });
    const updateOrdering = useHandler(
      async (
        updatedViews: ConsolidationView[],
        updatedFolders: ConsolidationFolder[],
      ) => {
        setViews(updatedViews);
        setFolders(updatedFolders);
        try {
          await client?.saveConsolidationViewsAndFolders({
            folders: updatedFolders,
            views: updatedViews,
          });
        } catch (error) {
          console.error("Error saving consolidation view ordering", error);
          await reloadViews();
        }
      },
    );
    const openCreateFolderModal = useHandler(() =>
      setShowCreateFolderModal(true),
    );
    const openEditFolderModal = useHandler((folderId: string) => {
      setEditingFolder(
        folders.find((folder) => folder._id === folderId) ?? null,
      );
    });
    const reservedFolderNames = useMemo(
      () => new Set(folders.map((folder) => folder.name)),
      [folders],
    );

    return (
      <ConsolidationViewsContext.Provider
        value={{
          folders,
          handleDeleteFolder,
          hasInitiallyLoaded,
          loadError,
          openCreateFolderModal,
          openEditFolderModal,
          updateOrdering,
          views,
        }}
      >
        <ReloadConsolidationViewsContext.Provider value={reloadViews}>
          <ConsolidationViewCountsContext.Provider value={counts}>
            {children}
          </ConsolidationViewCountsContext.Provider>
          <CreateOmsViewFolderModal
            isOpen={showCreateFolderModal}
            onClose={() => setShowCreateFolderModal(false)}
            onSubmit={handleCreateFolder}
            reservedFolderNames={reservedFolderNames}
          />
          {editingFolder && (
            <EditOmsViewFolderModal
              deleteFolder={handleDeleteFolder}
              isOpen
              onClose={() => setEditingFolder(null)}
              onSubmit={handleEditFolder}
              reservedFolderNames={reservedFolderNames}
              selectedFolder={editingFolder}
            />
          )}
        </ReloadConsolidationViewsContext.Provider>
      </ConsolidationViewsContext.Provider>
    );
  },
);
